import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import capitalize from 'lodash/capitalize'
import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from 'shop/components/Controls'
import { RadioInput } from 'shop/components/Inputs'
import BottomModal from 'shop/components/Modal/BottomModal'
import CenterModal from 'shop/components/Modal/CenterModal'
import { useMediaQueries, useShop } from 'shop/hooks'
import { DELIVERY_FULFILLMENT, PICKUP_FULFILLMENT } from 'shop/types'
import { LandingContext } from '../../LandingContext'
import { StoreRankingCriteria } from '../../types'
import FormInput from 'shop/components/Inputs/FormInput'
import { format } from 'date-fns'
import { createReadableDateString } from 'shop/utils'
import StoreSortDatetimeSelect from './StoreSortDatetimeSelect'
import { defaultTheme } from 'shop/theme'
import { OptionHeader } from './commonStyles'
import TimeSelectDropdown from './TimeSelectDropdown'
import DateTimeFlexibilityButtons from './DateTimeFlexibilityButtons'

type Props = {
  handleClose: () => void
}

type FulfillmentTypeSorts =
  | typeof DELIVERY_FULFILLMENT
  | typeof PICKUP_FULFILLMENT
  | 'ALL'

type FulfillmentDateOptionsSorts = 'EARLIEST' | 'SCHEDULE'

const getDefaultSelectedFulfillmentType = (
  sortCriteria: StoreRankingCriteria
): FulfillmentTypeSorts => {
  const { fulfillmentTypes } = sortCriteria
  if (fulfillmentTypes.length === 1) {
    return fulfillmentTypes[0] as FulfillmentTypeSorts
  }
  return 'ALL'
}

const StoreSortModal = ({ handleClose }: Props) => {
  const { sortCriteria, setSortCriteria } = useContext(LandingContext)
  const { partner } = useShop()
  const [selectedFulfillmentType, setSelectedFulfillmentType] =
    useState<FulfillmentTypeSorts>(
      getDefaultSelectedFulfillmentType(sortCriteria)
    )
  const [selectedFulfillmentDateOptions, setSelectedFulfillmentDateOptions] =
    useState<FulfillmentDateOptionsSorts>(
      sortCriteria.fulfillmentDate ? 'SCHEDULE' : 'EARLIEST'
    )
  const [showDatetimeSection, setShowDatetimeSection] = useState<boolean>(false)
  const [selectedFulfillmentDate, setSelectedFulfillmentDate] = useState<Date>(
    sortCriteria.fulfillmentDate
      ? new Date(sortCriteria.fulfillmentDate)
      : new Date()
  )
  const [selectedFulfillmentTime, setSelectedFulfillmentTime] = useState<
    string | null
  >(sortCriteria.fulfillmentTime || null)
  const [selectedFlexibilitySeconds, setSelectedFlexibilitySeconds] = useState<
    number | null
  >(sortCriteria.flexibilitySeconds || null)

  const { isMobile } = useMediaQueries()

  const handleApplySort = () => {
    let tempSortCriteria: StoreRankingCriteria = {
      fulfillmentTypes: [DELIVERY_FULFILLMENT, PICKUP_FULFILLMENT]
    }
    switch (selectedFulfillmentType) {
      case DELIVERY_FULFILLMENT:
        tempSortCriteria = { fulfillmentTypes: [DELIVERY_FULFILLMENT] }
        break
      case PICKUP_FULFILLMENT:
        tempSortCriteria = { fulfillmentTypes: [PICKUP_FULFILLMENT] }
        break
      default:
        tempSortCriteria = {
          fulfillmentTypes: [DELIVERY_FULFILLMENT, PICKUP_FULFILLMENT]
        }
    }

    if (selectedFulfillmentDateOptions === 'SCHEDULE') {
      if (selectedFulfillmentDate) {
        tempSortCriteria = {
          ...tempSortCriteria,
          fulfillmentDate: format(selectedFulfillmentDate, 'yyyy-MM-dd')
        }
      }
      if (selectedFulfillmentTime) {
        tempSortCriteria = {
          ...tempSortCriteria,
          fulfillmentTime: selectedFulfillmentTime
        }
      }
      if (selectedFlexibilitySeconds) {
        tempSortCriteria = {
          ...tempSortCriteria,
          flexibilitySeconds: selectedFlexibilitySeconds
        }
      }
    }

    setSortCriteria(tempSortCriteria)

    setTimeout(() => handleClose(), 300)
  }

  let modalContent = (
    <Container>
      <Header>Sort by</Header>
      <OptionsContainer>
        <OptionHeader>Order for</OptionHeader>
        <SortOption
          option={DELIVERY_FULFILLMENT}
          onChange={() => setSelectedFulfillmentType(DELIVERY_FULFILLMENT)}
          isChecked={selectedFulfillmentType === DELIVERY_FULFILLMENT}
        />
        <Divider />
        <SortOption
          option={PICKUP_FULFILLMENT}
          onChange={() => setSelectedFulfillmentType(PICKUP_FULFILLMENT)}
          isChecked={selectedFulfillmentType === PICKUP_FULFILLMENT}
        />
        <Divider />
        <SortOption
          option='ALL'
          onChange={() => setSelectedFulfillmentType('ALL')}
          isChecked={selectedFulfillmentType === 'ALL'}
        />
      </OptionsContainer>
      {(partner?.maxDaysInAdvance || 0) > 0 && (
        <OptionsContainer>
          <OptionHeader>When</OptionHeader>
          <SortOption
            option={'EARLIEST'}
            onChange={() => {
              setSelectedFulfillmentDateOptions('EARLIEST')
            }}
            isChecked={selectedFulfillmentDateOptions === 'EARLIEST'}
          />
          <Divider />
          <SortOption
            option={'SCHEDULE'}
            onChange={() => {
              setSelectedFulfillmentDateOptions('SCHEDULE')
            }}
            isChecked={selectedFulfillmentDateOptions === 'SCHEDULE'}
          >
            {selectedFulfillmentDateOptions === 'SCHEDULE' ? (
              <DatetimeInputs>
                <FormInput
                  value={createReadableDateString(
                    selectedFulfillmentDate,
                    'short'
                  )}
                  labelText='Date'
                  showError={false}
                  readOnly={true}
                  onClick={() => setShowDatetimeSection(true)}
                  style={{
                    cursor: 'pointer'
                  }}
                />
                <TimeSelectDropdown
                  selectedFulfillmentDate={selectedFulfillmentDate}
                  selectedFulfillmentTime={selectedFulfillmentTime}
                  setSelectedFulfillmentTime={setSelectedFulfillmentTime}
                />
              </DatetimeInputs>
            ) : undefined}
          </SortOption>
          {selectedFulfillmentDateOptions === 'SCHEDULE' && (
            <DateTimeFlexibilityButtons
              variant={!!selectedFulfillmentTime ? 'time' : 'date'}
              selectedFlexibilitySeconds={selectedFlexibilitySeconds}
              setSelectedFlexibilitySeconds={setSelectedFlexibilitySeconds}
            />
          )}
        </OptionsContainer>
      )}
      <ButtonsContainer>
        <Button variant='primary' onClick={handleApplySort}>
          Apply
        </Button>
      </ButtonsContainer>
    </Container>
  )

  if (showDatetimeSection) {
    modalContent = (
      <Container>
        <StoreSortDatetimeSelect
          selectedFulfillmentDate={selectedFulfillmentDate}
          setSelectedFulfillmentDate={setSelectedFulfillmentDate}
          setShowDatetimeSection={setShowDatetimeSection}
          maxDaysInAdvance={partner?.maxDaysInAdvance}
        />
      </Container>
    )
  }

  if (isMobile)
    return createPortal(
      <BottomModal
        handleCloseModal={handleClose}
        zIndex={(defaultTheme?.zIndex?.addressSelectionModal || 300) + 1}
        canClose={!showDatetimeSection}
      >
        {modalContent}
      </BottomModal>,
      document.body
    )

  return createPortal(
    <CenterModal
      handleCloseModal={handleClose}
      zIndex={(defaultTheme?.zIndex?.addressSelectionModal || 300) + 1}
      canClose={!showDatetimeSection}
    >
      {modalContent}
    </CenterModal>,
    document.body
  )
}

type SortOptionsProps = {
  children?: React.ReactNode
  option: FulfillmentTypeSorts | FulfillmentDateOptionsSorts
  onChange: () => void
  isChecked: boolean
}

const SortOption = ({
  children,
  option,
  onChange,
  isChecked = false
}: SortOptionsProps) => {
  return (
    <SortOptionItem>
      <RadioInput
        id={`${option}-sort-radio`}
        key={option}
        type='radio'
        name={option}
        value={option}
        onChange={onChange}
        disabled={false}
        checked={isChecked}
      />
      {!!children ? (
        children
      ) : (
        <SortOptionLabel htmlFor={`${option}-sort-radio`}>
          {capitalize(option.toLowerCase())}
        </SortOptionLabel>
      )}
    </SortOptionItem>
  )
}

const Container = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column'
}))
const Header = styled.h1<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  fontSize: '20px',
  fontWeight: 600,
  fontFamily: theme.fonts['heading'].family,
  padding: '18px 16px',
  borderBottom: '2px solid #EFEFF0',
  alignItems: 'center',

  [theme.mediaQueries.viewport7]: {
    padding: '20px 24px',
    fontSize: '24px',
    paddingBottom: '20px'
  },

  '& svg': {
    height: '20px',
    width: '20px',
    cursor: 'pointer'
  }
}))

const OptionsContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '16px 16px',

  [theme.mediaQueries.viewport7]: {
    padding: '24px 24px'
  }
}))

const SortOptionItem = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  gap: '22px',
  alignItems: 'center',
  padding: '0 10px',

  // fix FormInput InputContainer messing with Radio Buttons
  '>div': {
    flex: 1,
    width: 'unset'
  }
}))
const SortOptionLabel = styled.label<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flex: 1,
  height: '100%',
  cursor: 'pointer',
  fontSize: '14px',
  fontFamily: theme.fonts['body'].family,
  padding: '12px 0',

  // disable blue hightlight of user clicks
  WebkitTapHighlightColor: 'transparent',
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  userSelect: 'none',

  [theme.mediaQueries.viewport7]: {
    fontSize: '16px'
  }
}))

const ButtonsContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  flexDirection: 'row-reverse',
  padding: '16px 16px',

  [theme.mediaQueries.viewport7]: {
    padding: '8px 24px 24px',
    flexDirection: 'column'
  }
}))

const Divider = styled.hr<StyledHTMLElement>(
  (): CSSObject => ({
    height: '1px',
    width: '100%',
    backgroundColor: '#F0F0F0',
    border: 'none',
    margin: 0
  })
)

const DatetimeInputs = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  gap: '12px'
}))

export default StoreSortModal
